import { ProjectModel, SaleProjectEnum } from 'api-hooks/projects';
import { SaleLiteModel } from 'api-hooks/sale/common';
import { Option } from 'baseui/select';
import { ClockSVG } from 'common/assets';
import stylesColors from 'common/styles/styles.colors';
import { themedStyled } from 'common/styles/styletron.theme';
import Separator from 'components/common/separator';
import { ColumnDataType } from 'components/elements/table/type';
import TableComponent, {
  IColumn,
} from 'components/elements/table-component/table-component';
import Text from 'components/elements/text';
import useDate from 'hooks/use-date';
import useDrawer from 'hooks/use-drawer';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';

import RemoveInvoiceFormPanel from './remove-invoice-form-panel';

interface Props {
  data: SaleLiteModel[];
  option?: Option;
  project?: ProjectModel;
}

export default function SalesTable(props: Props) {
  const { t } = useTranslation();
  const { data } = props;
  const drawer = useDrawer();
  const formatDate = useDate();

  const columns = React.useMemo<IColumn<SaleLiteModel>[]>(
    () => [
      {
        header: t('common:created_at'),
        accessorKey: 'createdAt',
        minSize: 250,
        maxSize: 250,
        dataType: ColumnDataType.date,
        cell: ({ getValue }) => <>{formatDate(getValue() as Date)}</>,
      },
      {
        header: t('common:invoice_no'),
        accessorKey: 'invoiceNumber',
        minSize: 300,
        maxSize: 300,
        dataType: ColumnDataType.text,
        cell: ({ getValue, row }) => {
          const { original } = row;
          if (original.projectStatus === SaleProjectEnum.draft_removal) {
            return (
              <InvoiceContainer
                onClick={() => {
                  drawer.showCustom({
                    render: (onClose) => (
                      <RemoveInvoiceFormPanel
                        onClose={onClose}
                        saleData={original}
                        project={props.project}
                        options={props.option}
                      />
                    ),
                  });
                }}
              >
                <Text variant="LabelSmall">{getValue() as string}</Text>
                <ClockSVG />
              </InvoiceContainer>
            );
          }
          return <>{getValue() as string}</>;
        },
      },
      {
        header: t('common:code'),
        accessorKey: 'code',
        minSize: 250,
        maxSize: 250,
        dataType: ColumnDataType.text,
      },
      {
        header: t('common:description'),
        accessorKey: 'description',
        minSize: 300,
        maxSize: 300,
        dataType: ColumnDataType.text,
      },
      {
        header: t('common:customer'),
        accessorKey: 'customer.name',
        minSize: 250,
        maxSize: 250,
        dataType: ColumnDataType.text,
      },
    ],
    [drawer, props.option, props.project, t, formatDate],
  );

  return (
    <>
      <TableComponent
        columns={columns}
        data={data || []}
        headerColor={stylesColors.bg1}
        showBorder
      />
      <Separator gap={16} />
    </>
  );
}

const InvoiceContainer = themedStyled('div', {
  display: 'flex',
  gap: '8px',
});
